import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Custom cn that handles conditional logic + class merging
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs));
}

const monthMap: Record<string, number> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

export const isEventArchived = (date: string, month: string, year: number): boolean => {
  const day = parseInt(date);
  const monthIndex = monthMap[month];

  if (isNaN(day) || monthIndex === undefined || !year) return true;

  const eventDate = new Date(year, monthIndex, day);
  const today = new Date();

  // Remove time to compare only dates
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return eventDate < today;
};

interface Section {
  totalPages?: number;
  pageNo?: number;
  sections?: Section[];
}

export function findPaginationData(sections: Section[]): { totalPages: number; pageNo: number } | null {
  if (!sections || !Array.isArray(sections)) return null;

  for (const section of sections) {
    // Check if current section has pagination data
    if (section.totalPages !== undefined && section.pageNo !== undefined) {
      return {
        totalPages: section.totalPages,
        pageNo: section.pageNo
      };
    }

    // Recursively check nested sections
    if (section.sections && Array.isArray(section.sections)) {
      const nestedPagination = findPaginationData(section.sections);
      if (nestedPagination) return nestedPagination;
    }
  }

  return null;
}

export function cleanHtmlStyles(html: string) {
  return html.replace(/style="[^"]*"/gi, (match: string) => {
    const cleaned = match
      .replace(/font-family:[^;"]*;?/gi, '')
      .replace(/font-size:[^;"]*;?/gi, '')
      .replace(/color:[^;"]*;?/gi, '');
    return cleaned === 'style=""' ? '' : cleaned;
  });
}

export function cleanHtmlStylesExtreme(html: string): string {
  if (!html) return "";

  // Create a temporary DOM element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get just the text content, ignoring all HTML and styles
  return tempDiv.textContent || tempDiv.innerText || "";
}

export const saveCurrentUrl = () => {
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.pathname + window.location.search;
    sessionStorage.setItem('returnUrl', currentUrl);
    return currentUrl;
  }
  return '/';
};

export const getAndClearReturnUrl = (): string => {
  if (typeof window !== 'undefined') {
    const returnUrl = sessionStorage.getItem('returnUrl');
    sessionStorage.removeItem('returnUrl');
    return returnUrl || '/';
  }
  return '/';
};


export const getYouTubeVideoId = (url: string): string => {
  try {
    // Handle youtu.be format
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId || '';
    }
    
    // Handle youtube.com/watch format
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const videoId = urlParams.get('v');
      return videoId || '';
    }
    
    // Handle youtube.com/embed format (already correct)
    if (url.includes('youtube.com/embed')) {
      return url;
    }
    
    return '';
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error);
    return '';
  }
};

